import { Link } from '@tanstack/react-router';
import { useBrand } from '../../brand/BrandProvider';
import { isItcityHost } from '../../brand/host';
import { useCms, cmsText } from '../../api/cms';
import { SINGLE_FALLBACKS } from '../../content/fallbacks';
import { ITCityMark, Wordmark } from './BrandMarks';

/**
 * The shared marketing footer, in three brand identities.
 *
 *   reseller  — a reseller apex (white-label). Brand-safe columns only: no
 *               ITCity/Possignin links and no SSLCommerz banner (Decision #21).
 *   platform  — possignin.com and the platform chrome generally.
 *   itcity    — the ITCity parent-company page at itcitysoft.com / itcity.ltd.
 *
 * `variant="auto"` (the default) picks reseller vs platform from the resolved
 * host brand, which is what every MarketingShell page wants. `Company` passes
 * `variant="itcity"` explicitly, because that page is the parent-company
 * surface and keeps the ITCity mark rather than the Possignin wordmark.
 *
 * Structure and responsive behaviour are identical across all three: a
 * 2-column grid on phones widening to 4 on `sm`, so every column stays visible
 * on a narrow screen.
 *
 * COPY is CMS-editable; LINKS are not. Labels and destinations stay in code
 * deliberately — routes are typed, and a CMS-supplied destination would risk
 * shipping a dead link. Each identity reads its own key namespace so the three
 * surfaces are independently editable: `itcity.ltd` and `possignin.com` both
 * resolve to the platform CMS scope, so sharing keys between them would make
 * one edit change both.
 */

// Fixed to keep the build deterministic in tests.
const YEAR = 2026;

type FooterLink = { label: string; to: string; hash?: string };
type FooterCol = { heading: 'product' | 'company' | 'legal'; links: FooterLink[] };

const LEGAL_LINKS: FooterLink[] = [
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
  { label: 'Refund policy', to: '/refund' },
  { label: 'Cookies', to: '/cookies' },
];

const RESELLER_COLS: FooterCol[] = [
  {
    heading: 'product',
    links: [
      { label: 'Pricing', to: '/pricing' },
      { label: 'Contact', to: '/contact' },
      { label: 'Sign in', to: '/login' },
      { label: 'Start free', to: '/signup' },
    ],
  },
  { heading: 'legal', links: LEGAL_LINKS },
];

const PLATFORM_COLS: FooterCol[] = [
  {
    heading: 'product',
    links: [
      { label: 'Features', to: '/features' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Become a reseller', to: '/become-a-reseller' },
      { label: 'Sign in', to: '/login' },
      { label: 'Start free', to: '/signup' },
    ],
  },
  {
    heading: 'company',
    links: [
      { label: 'About ITCity', to: '/home' },
      { label: 'About Possignin', to: '/about' },
      { label: 'Blog', to: '/blog' },
    ],
  },
  { heading: 'legal', links: LEGAL_LINKS },
];

// ITCity's own columns. Everything the old bespoke Company footer linked to is
// preserved: Possignin + Blog here, "Become a reseller" under Product, Terms /
// Privacy under Legal, and Contact as the support mailbox in the brand block.
// "About ITCity" carries a hash so it scrolls to the Company details section
// instead of being a no-op self-link on the page it already sits on.
const ITCITY_COLS: FooterCol[] = [
  {
    heading: 'product',
    links: [
      { label: 'Features', to: '/features' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Become a reseller', to: '/become-a-reseller' },
      { label: 'Sign in', to: '/login' },
      { label: 'Start free', to: '/signup' },
    ],
  },
  {
    heading: 'company',
    links: [
      { label: 'Possignin', to: '/possignin' },
      { label: 'About ITCity', to: '/home', hash: 'company-details' },
      { label: 'Blog', to: '/blog' },
    ],
  },
  { heading: 'legal', links: LEGAL_LINKS },
];

const HEADING_FALLBACKS = { product: 'Product', company: 'Company', legal: 'Legal' } as const;

export function MarketingFooter({ variant = 'auto' }: { variant?: 'auto' | 'itcity' }) {
  const brand = useBrand();
  const { data: cms } = useCms();

  // `auto` on an ITCity host resolves to the ITCity identity, not the platform
  // one. Company passes variant="itcity" explicitly, but every OTHER page on
  // itcity.ltd (/features, /pricing, /about, /contact, legal) goes through
  // MarketingShell with the default — so they were all footing with the
  // Possignin wordmark, Possignin's columns and support@possignin.com.
  const isItcity = variant === 'itcity' || isItcityHost();
  const isReseller = !isItcity && !brand.isPlatform;

  // Each identity reads its own CMS namespace — see the note above on why
  // platform and itcity must not share keys.
  const ns = isItcity ? 'company' : isReseller ? 'landing' : 'shared';
  const taglineKey = `${ns}.footerTagline`;
  const headingsKey = `${ns}.footerHeadings`;
  const metaKey = `${ns}.footerMeta`;

  const cols = isItcity ? ITCITY_COLS : isReseller ? RESELLER_COLS : PLATFORM_COLS;
  const email = isItcity ? 'hello@itcitysoft.com' : brand.supportEmail;

  const tagline = cmsText(cms, taglineKey, 'tagline', SINGLE_FALLBACKS[taglineKey]!.tagline!);

  // A reseller's copyright is computed from the resolved brand rather than read
  // from a static fallback: a hardcoded "ITCity" default would render on every
  // white-label domain that hasn't set its own (the leak class that
  // RESELLER_LIST_OVERRIDES exists to prevent). The SINGLE_FALLBACKS entry is
  // only the editor's placeholder.
  const copyrightFallback = isReseller ? `© ${YEAR} ${brand.name}. All rights reserved.` : `© ${YEAR} ITCity. All rights reserved.`;
  const copyright = cmsText(cms, metaKey, 'copyright', copyrightFallback);
  const statusLabel = cmsText(cms, metaKey, 'statusLabel', SINGLE_FALLBACKS[metaKey]!.statusLabel!);
  // Trade licence — ITCity's own legal identity, so it renders on the ITCity and platform
  // footers ONLY. A reseller's white-label footer must never carry it (Decision #21): the
  // customer there is buying from the reseller's company, not from ITCity. A reseller that
  // needs its own number should get a per-reseller branding field, not this one.
  // Read from `company.details.registration` — the ONE editable source, shared with the
  // Company page's registered-details table and the About page, so an admin edit can never
  // leave two surfaces quoting different licence numbers.
  const tradeLicense = isReseller
    ? ''
    : cmsText(cms, 'company.details', 'registration', SINGLE_FALLBACKS['company.details']!.registration!);
  const paymentsLabel = cmsText(cms, metaKey, 'paymentsLabel', 'We accept');

  return (
    <footer className="border-t border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-2 sm:grid-cols-4 gap-8 lg:gap-10">
        <div className="col-span-2 sm:col-span-1">
          {isItcity ? <ITCityMark /> : <Wordmark />}
          <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed max-w-[17rem]">{tagline}</p>
          <a
            href={`mailto:${email}`}
            className="mt-4 inline-block text-sm font-medium text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 transition-colors"
          >
            {email}
          </a>
        </div>
        {cols.map((c) => {
          const title = cmsText(cms, headingsKey, c.heading, HEADING_FALLBACKS[c.heading]);
          return (
            <nav key={c.heading} aria-label={title}>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {title}
              </h4>
              <ul className="mt-4 space-y-2.5">
                {c.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      hash={l.hash}
                      className="text-sm text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          );
        })}
      </div>
      {/* Accepted payment methods (SSLCommerz merchant requirement). Self-hosted asset —
          never hotlink a third party's CDN. Platform + ITCity hosts only: the gateway is
          ITCity's merchant account, and a reseller's brand-safe surface stays free of it
          (Decision #21). Flip the guard to show it everywhere if that policy ever changes. */}
      {!isReseller && (
        <div className="border-t border-zinc-200 dark:border-zinc-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col items-center gap-2">
            <span className="text-xs text-zinc-500 dark:text-zinc-400">{paymentsLabel}</span>
            <img
              src="/sslcommerz-pay-with.webp"
              alt="Pay with SSLCommerz — accepted cards, mobile banking and internet banking"
              width={2048}
              height={240}
              loading="lazy"
              decoding="async"
              className="w-full max-w-2xl h-auto"
            />
          </div>
        </div>
      )}
      <div className="border-t border-zinc-200 dark:border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500 dark:text-zinc-400">
          <div className="flex flex-col items-center sm:items-start gap-1">
            <p className="tabular-nums">{copyright}</p>
            {tradeLicense && <p className="tabular-nums">{tradeLicense}</p>}
          </div>
          <p className="inline-flex items-center gap-1.5">
            <span className="relative flex w-1.5 h-1.5" aria-hidden="true">
              <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-60 motion-safe:animate-ping" />
              <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500" />
            </span>
            {statusLabel}
          </p>
        </div>
      </div>
    </footer>
  );
}
